/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

import * as go from "gojs";
import * as solutionGraph from "./solutionGraph";
import { defineCustomElements } from "@esri/calcite-components/dist/loader";
import { ISolutionGraphTranslations, ITemplateInfoGraph } from "./solutionGraph";
import { setAssetPath as setAssetPathForCalciteComponents } from "@esri/calcite-components/dist/components";
import { setAssetPath as setAssetPathForSolutionComponents } from "@esri/solutions-components/dist/components";

interface IKeyedGraphObject{
  [key: string]: go.GraphObject;
}

const cNodeCornerRadius = 8;
// eslint-disable-next-line no-bitwise
const cNodeCornerRadiusCornerSelectionBitmap = 1 | 2 | 4 | 8;  // all corners
const cNodeHighlightColor = "#1e90ff";
const cNodeHighlightStrokeWidth = 4;
go.Diagram.licenseKey = "298647e0b16f4fc702d90676423d6bf919a175639c841aa45d0412f3ed083d06329fef2851d38f9787af4efe187c90d1ddc06e219e49563fe531d3da13e1d1aab03774e5140b4589f31136c5ccaa2ca1ae2870e0d2b676a1df678bedebaa";

// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Manages the display of and interactio between a solution's index and diagram.
 */
export class SolutionDisplay {
  protected allNodesAndLinks!: solutionGraph.INodesAndLinks;
  protected collection!: ITemplateInfoGraph[];
  protected currentSelectedGroupId = "";
  protected currentSelectedItemId = "";
  protected diagram!: go.Diagram;
  protected diagramNeedsRedraw = false;
  protected diagramNodeHighlighterIndex!: solutionGraph.IKeyedAdornment;
  protected diagramNodeIndex!: IKeyedGraphObject;
  protected index!: HTMLSolutionItemAccordionElement;
  protected usefilteredView = false;

  /**
   * Creates a new instance of the SolutionDisplay class.
   * @param indexPanel The Calcite panel element to use as the index panel.
   * @param diagramPanel The Calcite panel element to use as the diagram panel.
   * @example &lt;caption>Expected environment&lt;/caption>
   *   &lt;calcite-shell>
   *     &lt;calcite-shell-panel id="indexPanel" slot="panel-start" width-scale="m"/>
   *     &lt;calcite-panel id="diagramPanel" style="display:flex;"/>
   *   &lt;/calcite-shell>
   */
  public constructor(
    protected indexPanel: HTMLCalciteShellPanelElement,
    protected diagramPanel: HTMLCalcitePanelElement,
    protected diagramTranslations: ISolutionGraphTranslations
  ) {
    // Tell Calcite and Solutions components where to find assets
    const assetsPath = window.location.href;
    void defineCustomElements(window);
    setAssetPathForCalciteComponents(assetsPath);
    setAssetPathForSolutionComponents(assetsPath);

    // Make sure that the panels are empty
    this.indexPanel.innerHTML = "";
    this.diagramPanel.innerHTML = "";
  }

  /**
   * Creates the index and diagram for the given collection of template info.
   * @param collection The collection of template info to display.
   * @returns A promise that resolves when the index and diagram have been created.
   */
  public create(collection: ITemplateInfoGraph[]): void {
    this.collection = collection;

    // Create the index
    this.index = document.createElement("solution-item-accordion");
    this.index.listSelectionMode = "single";
    this.index.style.overflowY = "auto";
    this.indexPanel.appendChild(this.index);
    this.index.templateInfos = collection;

    // Create the model data that will be represented by Nodes and Links in the diagram
    this.allNodesAndLinks = solutionGraph.getNodesAndLinks(collection);

    // Create the full diagram
    this.diagram = solutionGraph.drawSolutionGraph(
      this.diagramPanel, this.allNodesAndLinks, this.diagramTranslations);

    // Create an index into the diagram's nodes for highlighting purposes, including a highlighter for each node
    this.createNodeHighlighters();

    // Wire the index and the diagram together
    document.addEventListener("clickInDiagram", this.handle_clickInDiagram);
    document.addEventListener("clickOnNode", this.handle_clickOnNode);
    document.addEventListener("filteredViewChanged", this.handle_filteredViewChanged);
    document.addEventListener("reset", this.handle_reset);
    this.index.addEventListener("solutionItemSelect", this.handle_solutionItemSelect);
  }

  /**
   * Destroys the index and diagram; removes all event listeners.
   */
  public dispose() {
    document.removeEventListener("clickInDiagram", this.handle_clickInDiagram);
    document.removeEventListener("clickOnNode", this.handle_clickOnNode);
    document.removeEventListener("filteredViewChanged", this.handle_filteredViewChanged);
    document.removeEventListener("reset", this.handle_reset);
    this.index.removeEventListener("solutionItemSelect", this.handle_solutionItemSelect);

    this.indexPanel.innerHTML = "";
    this.diagramPanel.innerHTML = "";
  }

  // ---------------------------------------------------------------------------------------------------------------- //

  /**
   * Clears all selections in the index and diagram.
   * @param alsoCollapseAllAccordionItems Whether to collapse all accordion items in the index
   */
  protected clearSelections(
    alsoCollapseAllAccordionItems = true
  ) {
    void this.index.deselectAll();
    if (alsoCollapseAllAccordionItems) {
      void this.index.collapseAll();
    }

    const nodes = this.getNodesFromDiagram(this.diagram);
    const nodeIds = nodes.map((node: go.GraphObject) => (node as go.Node).data.key);
    solutionGraph.highlightNodes(nodeIds, nodes, false, this.diagramNodeHighlighterIndex);
  }

  /**
   * Creates indicies into the diagram's nodes and node highlighters.
   * Rebuilds this.diagramNodeIndex and this.diagramNodeHighlighterIndex.
   */
  protected createNodeHighlighters() {
    this.diagramNodeIndex = {};
    this.diagramNodeHighlighterIndex = {};

    const nodes = this.getNodesFromDiagram(this.diagram);
    nodes.forEach((node: go.Node) => {
      this.diagramNodeIndex[node.data.key] = node;

      // Create the highlighter symbols for graph nodes
      const nodeHighlighter = new go.Adornment("Auto", {
        background: "transparent",
        margin: 2
      });
      nodeHighlighter.add(new go.Shape("RoundedRectangle", {
        fill: "transparent",
        parameter1: cNodeCornerRadius,
        parameter2: cNodeCornerRadiusCornerSelectionBitmap,
        stroke: cNodeHighlightColor,
        strokeWidth: cNodeHighlightStrokeWidth
      }));
      nodeHighlighter.add(new go.Placeholder({
        margin: 1
      }));
      this.diagramNodeHighlighterIndex[node.data.key] = nodeHighlighter;
    });
  }

  /**
   * Retrieves a node from the diagram by its id.
   * @param diagram The diagram to search
   * @param id Id of node to find
   * @returns Node matching the given id, or undefined if not found
   */
  protected getNodeById(
    // diagram: go.Diagram,
    id: string
  ): go.Node | undefined {
    return this.diagramNodeIndex[id] as go.Node;
  }

  /**
   * Retrieves all nodes from the diagram.
   * @param diagram The diagram to search
   * @returns Array of nodes in the diagram
   */
  protected getNodesFromDiagram(
    diagram: go.Diagram
  ): go.Node[] {
    const nodes: go.Node[] = [];
    const nodeIterator = diagram.nodes.iterator;
    while (nodeIterator.next()) {
      nodes.push(nodeIterator.value);
    }
    return nodes;
  }

  /**
   * Handles a click in the diagram.
   * @param evt Event object
   */
  protected handle_clickInDiagram = (evt: Event) => {
    evt.stopImmediatePropagation();
    if (this.currentSelectedGroupId || this.currentSelectedItemId) {
      this.currentSelectedGroupId = "";
      this.currentSelectedItemId = "";
      this.diagramNeedsRedraw = this.usefilteredView;
      this.redraw();
    }
  }

  /**
   * Handles a click on a node in the diagram.
   * @param evt Event object
   */
  protected handle_clickOnNode = (evt: Event) => {
    evt.stopImmediatePropagation();
    const id = (evt as CustomEvent).detail.data.key;
    this.redraw(id, id === this.currentSelectedItemId);
  }

  /**
   * Handles a change in the filter selected view button.
   * @param evt Event object
   */
  protected handle_filteredViewChanged = (evt: Event) => {
    this.usefilteredView = (evt as CustomEvent).detail;
    this.diagramNeedsRedraw = !!this.currentSelectedGroupId || !!this.currentSelectedItemId;
    this.redraw(this.currentSelectedItemId || this.currentSelectedGroupId);
  }

  /**
   * Handles a reset of the diagram.
   * @param evt Event object
   */
  protected handle_reset = (evt: Event) => {
    evt.stopImmediatePropagation();
    this.reset();
  }

  /**
   * Handles a selection of an item in the index.
   * @param evt Event object
   */
  protected handle_solutionItemSelect = (evt: Event) => {
    evt.stopImmediatePropagation();
    const id = (evt as CustomEvent).detail.itemId;
    this.redraw(id, id === this.currentSelectedItemId || id === this.currentSelectedGroupId, false);
  }

  /**
   * Selects an item to highlight or unhighlight.
   * @param id Id of selected item
   * @param toggleSelection Whether to toggle the selection state of the item
   * @param alsoCollapseAllAccordionItems Whether to collapse all accordion items in the index
   */
  protected redraw(
    id = "",
    toggleSelection = false,
    alsoCollapseAllAccordionItems = true
  ): void {
    const areDrawingAGroup = this.allNodesAndLinks.groupPseudoNodes.includes(id);

    if (this.diagramNeedsRedraw) {
      // Redraw the full diagram
      solutionGraph.drawDiagram(this.diagram, this.allNodesAndLinks);
      this.createNodeHighlighters();
      this.diagramNeedsRedraw = false;
    }
    let needsReset = false;

    // Clear all selections in index and diagram
    this.clearSelections(alsoCollapseAllAccordionItems);

    if (!id) {
      // Nothing is currently selected
      return;
    }

    // Group or item selected?
    if (areDrawingAGroup) {
      this.currentSelectedItemId = "";

      // Group nodes don't have corresponding diagram nodes
      if (this.currentSelectedGroupId !== id) {
        this.currentSelectedGroupId = id;
      } else if (toggleSelection){
        // Toggle currently-selected node
        this.currentSelectedGroupId = this.currentSelectedGroupId ? "" : id;
        needsReset = this.usefilteredView && !this.currentSelectedGroupId;
      }

      if (this.currentSelectedGroupId) {
        // If we're using the filter selected view, rebuild the diagram so that we can get the group nodes
        if (this.usefilteredView) {
          solutionGraph.drawDiagram(this.diagram, this.allNodesAndLinks);
          this.createNodeHighlighters();
        }

        // Get the nodes in the group
        const group = this.collection.filter((item: ITemplateInfoGraph) => item.id === id)[0];
        const groupNodes = group.dependencies.map((dependency: string) => this.diagramNodeIndex[dependency]);

        if (this.usefilteredView) {
          // Update the diagram with just the group items
          const nodes: solutionGraph.INode[] = groupNodes.map((node: go.GraphObject) => (node as any).data);
          solutionGraph.drawDiagram(this.diagram, { nodes, links: [], groupPseudoNodes: [] });
        } else {
          // Highlight group items in diagram, but not their connected items or links
          solutionGraph.highlightNodes(group.dependencies, groupNodes, true, this.diagramNodeHighlighterIndex);
        }

        void this.index.selectItem(this.currentSelectedGroupId);
      }

    } else {
      this.currentSelectedGroupId = "";

      // Item nodes
      if (this.currentSelectedItemId !== id) {
        this.currentSelectedItemId = id;
      } else if (toggleSelection){
        // Toggle currently-selected node
        this.currentSelectedItemId = this.currentSelectedItemId ? "" : id;
        needsReset = this.usefilteredView && !this.currentSelectedItemId;
      }

      let focusNode = this.getNodeById(id);
      if (!focusNode || this.usefilteredView) {
        // Item is not in the current diagram because we're in filter selected view with other items
        // Return to the complete model and try again
        solutionGraph.drawDiagram(this.diagram, this.allNodesAndLinks);
        this.createNodeHighlighters();
        focusNode = this.getNodeById(id);
        if (!focusNode) {
          return;
        }
      }

      if (this.currentSelectedItemId) {
        if (this.usefilteredView) {
          // If we're using the filter selected view, assemble the nodes and links connected to this node
          const connectedNodesAndLinks = solutionGraph.getConnectedNodesAndLinks(focusNode);
          connectedNodesAndLinks.connectedNodes.add(focusNode);

          // Update the diagram with just the connected nodes and links
          const nodes: solutionGraph.INode[] = Array.from(connectedNodesAndLinks.connectedNodes).map((node: go.Node) => node.data);
          const links: solutionGraph.ILink[] = Array.from(connectedNodesAndLinks.connectedLinks).map((link: go.Link) => link.data);
          solutionGraph.drawDiagram(this.diagram, { nodes, links, groupPseudoNodes: [] });
          this.createNodeHighlighters();

          // Get node instance in new diagram
          focusNode = this.getNodeById(id);
          if (!focusNode) {
            return;
          }
        }

        // Highlight node in diagram; need to delay to avoid timing problem with full-diagram draw above
        if (focusNode) {
          setTimeout(() => solutionGraph.highlightNode(focusNode as go.GraphObject, true, this.diagramNodeHighlighterIndex[id]), 1);
        }

        void this.index.selectItem(this.currentSelectedItemId);
      }
    }

    // If we've deselected an item and we're in filter selected view, draw the full diagram
    if (needsReset) {
      this.reset();
    }
  }

  /**
   * Clears all selections in the index and diagram.
   */
  protected reset() {
    this.diagramNeedsRedraw = false;

    this.currentSelectedGroupId = "";
    this.currentSelectedItemId = "";

    // Return to the complete model
    solutionGraph.drawDiagram(this.diagram, this.allNodesAndLinks);
    this.createNodeHighlighters();

    // Clear all selections in index and diagram
    this.clearSelections();
  }
}
